/**
 * PLAYBACK — the one place that answers "is there audio to play, and if not, why not".
 *
 * ============================================================================
 * THIS MODULE PLAYS NOTHING AND FETCHES NOTHING. It reads `audioUrl` off a record and
 * reports what is missing. There is no endpoint behind it, and that is not an omission:
 * audio does not have a route of its own, it arrives ON the song (`Song.audioUrl`) and on
 * its versions (`SongVersion.audioUrl`), so the "request" for playable audio is the read
 * that already happens. What was missing was somewhere to ASK.
 * ============================================================================
 *
 * WHY IT EXISTS (Batch 7 §9). The brief is explicit that the frontend must be able to
 * consume a future real `audioUrl` and real playback state "once the backend supplies it",
 * and that any boundary adjustment belongs "at the service/domain layer rather than inside
 * every player component". Before this module the question was answered in three
 * incompatible ways and restated in a dozen comments:
 *
 *   - routes/Share.tsx branched on `shared.audioUrl` inline — the app's ONLY consumer of a
 *     real URL, and the only place that would light up the day one exists.
 *   - api/publisherApi.describeAudioBlocker asked the same thing for distribution, with its
 *     own sentence, scoped to the Publisher.
 *   - every other surface — the mini player, TrackPlayer, LyricSheet, the Studio panels —
 *     carried a comment saying `Song.audioUrl` is null and rendered as if it always would be.
 *
 * The third is the problem: a comment is not a seam. Twelve components independently
 * knowing "there is never audio" is twelve places to edit when there is, and each is a
 * chance to miss one. This module is the single read, so a real URL reaches every surface
 * that consults it without any of them changing.
 *
 * WHAT IS DELIBERATELY NOT CHANGED HERE, and §11 says so twice: the mini player. It is
 * accepted as it stands, it gains no `<audio>` element (components/__tests__/MiniPlayer.test.tsx
 * asserts the absence), and nothing in this module pushes one on it. The simulated transport
 * in stores/playerStore.ts and hooks/usePlayer.ts is likewise untouched — see `simulated`
 * below, which reports it rather than replacing it.
 *
 * WHAT WOULD CHANGE THE ANSWER. Nothing in the frontend. `playableAudioUrl` returns
 * whatever the record carries, so the day `POST /generations` returns a song with an
 * `audioUrl` — or `GET /songs/:id` does — this starts returning a string and
 * `hasPlayableAudio` starts returning true, with no edit here or at any call site. That is
 * the whole design: there is no flag to flip, because a flag would be a second source of
 * truth that could disagree with the data.
 *
 * See docs/api-contract.md §29 for the player boundary, and §19/§30 for the two adjacent
 * capabilities (editing audio, and producing a file from it) which this does NOT grant:
 * being able to play a track is not being able to cut one or export one.
 */
import { API_BASE_URL } from '../config';
import type { AudioAsset } from '../types/domain';

/**
 * Anything that might carry playable audio.
 *
 * Structural rather than `Song`, and that is the point: `Song`, `SongVersion` and
 * `shareApi.SharedSong` all carry `audioUrl` and all three need this answer, but the share
 * page's payload is deliberately narrower than a Song (see shareApi) and must not be
 * widened to fit a helper. `AudioAsset` names the field `url` instead — see
 * `audioSourceFromAsset`.
 */
export interface AudioSource {
  audioUrl?: string | null;
}

/**
 * One reason a track cannot be listened to.
 *
 * The same record shape as `AudioUploadBlocker`, `CoverBlocker` and `LyriaBlocker`,
 * deliberately: the UI already knows how to render a list of these, and a reviewer asking
 * "what is missing?" should get the answer in the same form from every boundary that has one.
 */
export interface PlaybackBlocker {
  /** Short identifier, for tests and for keys. */
  id: 'no-audio-file' | 'no-generated-audio' | 'no-backend';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/**
 * The URL to play, or null when there is nothing.
 *
 * **THE SEAM.** One expression, and it is a read of the record rather than a decision:
 * `?? null` normalises the absent field to the same value the null one has, because
 * "the payload omitted it" and "the backend says there is no audio yet" are the same
 * situation for a caller and should not be two branches at every call site.
 *
 * Null for every song in this build, because generation is simulated and nothing has ever
 * produced a file — see api/generationApi.ts, which sets `audioUrl: null` explicitly for
 * exactly this reason. It is null because the DATA says so, not because this function
 * says so, which is what makes it a seam and not a stub.
 */
export function playableAudioUrl(source: AudioSource | null | undefined): string | null {
  return source?.audioUrl ?? null;
}

/** Whether there is something to play. False throughout this build. */
export function hasPlayableAudio(source: AudioSource | null | undefined): boolean {
  return playableAudioUrl(source) !== null;
}

/**
 * Reads an `AudioAsset` as an audio source.
 *
 * `types/domain.AudioAsset` is the shape a backend returns for a master or a stem, and it
 * names its URL `url` where `Song` names it `audioUrl`. One adapter rather than a union
 * type with a runtime discriminator: the two names are a fact about two payloads, and
 * translating between them is a line of code, not a type-system problem.
 *
 * Nothing constructs an `AudioAsset` in this build — see that type's own header — so this
 * has no caller in src/ yet and is exercised only by its test. It is here so that when
 * stems or a master arrive as assets, they reach the same predicate the song's own audio
 * does instead of a second one written beside them.
 */
export function audioSourceFromAsset(asset: AudioAsset): AudioSource {
  return { audioUrl: asset.url };
}

/**
 * Everything standing between a record and a listener, in order.
 *
 * Computed rather than constant, matching `describeAudioUploadBlockers`: the day
 * `VITE_API_BASE_URL` is set the last entry disappears on its own, and the day a song
 * carries a URL the first does. The middle one does NOT disappear with configuration —
 * a base URL says a server answers, not that anything behind it generates audio — and
 * that is why it is listed separately from the backend.
 *
 * ORDERED MOST SPECIFIC FIRST. A caller rendering only `blockers[0]` gets the reason that
 * is about THIS track, which is the one a user asked about; the general ones follow for a
 * reader who wants to know why the situation exists at all.
 */
export function describePlaybackBlockers(
  source: AudioSource | null | undefined
): PlaybackBlocker[] {
  /*
    EMPTY THE MOMENT THERE IS A URL, AND THAT IS THE WHOLE STRUCTURE — including the
    configuration blocker below, which is nested inside this check rather than sitting beside
    it. It reads as though it belongs outside: `VITE_API_BASE_URL` being unset is a fact about
    the build, exactly as it is in `describeAudioUploadBlockers` where it IS a separate
    top-level entry. The difference is that an upload needs a server to receive it, and
    playback does not need one to serve it: a URL on the record is the delivery, whether it
    points at an asset host, a CDN or a signed bucket, and the browser streams it without
    consulting the app's API at all.

    So a configured base URL is neither necessary nor sufficient here, and listing it beside a
    working URL would put "playback unavailable" under a functioning player. What the
    invariant buys is that `blockers.length === 0` and `streamable` cannot disagree — the same
    property `audioUploadApi.supports()` derives its answer from.
  */
  if (hasPlayableAudio(source)) return [];

  const blockers: PlaybackBlocker[] = [
    {
      id: 'no-audio-file',
      summary: 'This track has no audio file, so there is nothing to stream.',
      resolution:
        'A generation backend that returns a stored, playable URL on the song record. ' +
        '`Song.audioUrl` is the field, and it is already read wherever it would be needed.',
    },
    /*
      TWO ENTRIES, NOT ONE, and the distinction is what makes the second worth having. The
      first says something about a RECORD; this says something about the BUILD. "This one
      isn't ready" and "none of them can be" read very differently to whoever is looking at
      the notice, and collapsing them would lose the sentence that actually explains the
      situation.
    */
    {
      id: 'no-generated-audio',
      summary:
        'Lyrixsa produces no audio at this stage — generation is simulated, so songs carry ' +
        'metadata, lyrics and artwork but no recording.',
      resolution:
        'A real generation provider behind POST /generations, returning audio the backend ' +
        'stores. See api/generationApi.ts and docs/api-contract.md §8.',
    },
  ];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary: 'There is no backend configured, so no audio could be served even if it existed.',
      resolution:
        'Set VITE_API_BASE_URL and serve audio from storage the browser can stream from. ' +
        'Every module in src/api/ is a fixture until then.',
    });
  }

  return blockers;
}

/** What a caller needs to know before it offers a play control. */
export interface PlaybackSupport {
  /** The URL to hand an audio element, or null. */
  audioUrl: string | null;
  /** Whether a real audio element could be given something to play. False throughout. */
  streamable: boolean;
  /**
   * Whether the in-app transport is the simulated clock rather than an audio element.
   *
   * Always `true`, and typed as the literal so a reader cannot mistake it for a runtime
   * question — the same device `AUDIO_EDIT_CAPABILITIES.supportedByLyria3` uses. The
   * transport in stores/playerStore.ts advances a counter; `elapsed`, seeking, queue
   * advance and the 0.5×–2× rate are all real STATE and all genuinely move, and none of
   * them make a sound. §11 keeps that as it is; this field reports it so a surface can
   * say so without asserting it independently.
   */
  simulated: true;
  /** Everything missing, most specific first. Empty only when a track really can be played. */
  blockers: PlaybackBlocker[];
}

export const playbackApi = {
  /**
   * What playback can do for one record right now, for the UI to read.
   *
   * The same shape and purpose as `audioUploadApi.support()` and `coverApi.support()`: the
   * copy on screen comes from the boundary that knows, rather than from a sentence
   * hardcoded in a component that nobody will update the day audio appears.
   *
   * Synchronous, unlike the mocks in this directory, and for the reason the header gives —
   * there is no request here. `audioUrl` arrives with the song, so asking about it is not
   * a fetch and making it async would put a promise in front of a property read at every
   * call site for no future benefit.
   */
  support(source: AudioSource | null | undefined): PlaybackSupport {
    return {
      audioUrl: playableAudioUrl(source),
      streamable: hasPlayableAudio(source),
      simulated: true,
      blockers: describePlaybackBlockers(source),
    };
  },

  /**
   * Whether this record can be streamed.
   *
   * Derived from the record and NOT from `USE_MOCK_API`, for the reason
   * `audioUploadApi.supports()` and `audioEditApi.supports()` both give: pointing
   * VITE_API_BASE_URL at a server does not give it audio, and returning true because a
   * base URL was set is the "looks like it works" failure these boundaries exist to
   * prevent. Unlike those two this is not hardcoded false either — the data decides, so
   * nothing has to be flipped by hand.
   */
  supports(source: AudioSource | null | undefined): boolean {
    return hasPlayableAudio(source);
  },
};
